import { Doc } from '@dadohr/shared/domainTypes/document';
import { SlackIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/slack';
import styled from '@emotion/styled';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { InfoBox } from '../../../../../../components/InfoBox';
import { Loader } from '../../../../../../components/Loader';
import { useErrorLogger } from '../../../../../../hooks/useErrorLogger';
import { usePromise } from '../../../../../../hooks/usePromise';
import { useTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { removeDoc } from '../../../../../../services/firebase';
import { useFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { ONE_MINUTE } from '../../../../../../services/time';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties
} from '../common';
import { useSlackConfig } from './service';


const NAME = 'Slack';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const InitialSetupDialog = ({
  open,
  onClose,
  config
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  config: Doc<SlackIntegrationConfig>;
}) => {
  const { t } = useTranslation();
  const [installHref] = usePromise(() => {
    return fetch(`/slack/install`).then(async (x) => {
      const text = await x.text();
      const parser = new DOMParser();
      const dom = parser.parseFromString(text, 'text/html');
      const a = dom.getElementsByTagName('a')[0];
      return a.href || '';
    });
  }, []);
  if (!installHref && window.location.host.startsWith('localhost')) {
    console.error(
      'This is using a rewrite rule to retrieve the installHref - it cannot be used from localhost. Please use a deployed version to set up the integration'
    );
  }
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('org.integrations.slack.setupInfo.p1')}
        </Alert>
        <Typography component="p" variant="body1">
          {t('org.integrations.slack.setupInfo.p2')}
        </Typography>
        <Typography component="p" variant="caption">
          {t('org.integrations.slack.setupInfo.p2-1')}
        </Typography>

        <Typography component="p" variant="body1">
          {t('org.integrations.slack.setupInfo.p2-2')}
        </Typography>
        <InfoBox>{t('org.integrations.slack.setupInfo.p3')}</InfoBox>
      </Content>
      <DialogActions>
        {installHref ? (
          <Button
            variant="contained"
            color="primary"
            target="_blank"
            rel="noopener"
            href={installHref}
          >
            {t('org.integrations.actions.setup')}
          </Button>
        ) : (
          <div />
        )}
      </DialogActions>
    </Dialog>
  );
};

const ExistingSetupDialog = ({
  open,
  onClose,
  orgId,
  config
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  config: Doc<SlackIntegrationConfig>;
}) => {
  const { t } = useTranslation();
  const [workspaces, loading] = useFirebaseFunction<{
    teams: {
      id: string;
      name: string;
      icon: {
        image_34: string;
        image_44: string;
        image_102: string;
      };
    }[];
  }>(
    'integrations_slack-getWorkspaceList',
    { orgId },
    [orgId],
    ONE_MINUTE * 5,
    true
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('org.integrations.slack.setupInfo.p1')}
        </Alert>
        <Alert severity="success" icon={false}>
          {t('org.integrations.slack.existingSetup.info')}
        </Alert>
        {loading && <Loader height={100} />}
        {workspaces && (
          <List>
            {workspaces.teams.map((t) => (
              <ListItem key={t.id}>
                <ListItemAvatar>
                  <Avatar src={t.icon.image_34} variant="square" />
                </ListItemAvatar>
                <ListItemText>{t.name}</ListItemText>
              </ListItem>
            ))}
          </List>
        )}
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeDoc(config)}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

interface SetupDialogProps
  extends IntegrationSetupDialogWithConfigProperties<SlackIntegrationConfig> {
  orgId: string;
}

export const SetupDialog = ({
  config,
  onClose,
  orgId,
  onSuccessfulIntegration, // can be ignored since this integration is not an import source
  open
}: SetupDialogProps) => {
  const setup =
    !!config?.data.botUserOAuthAccessToken && !!config?.data.installationId;

  if (!config) {
    return null;
  }
  return (
    <>
      {!setup && (
        <InitialSetupDialog
          orgId={orgId}
          config={config}
          open={open}
          onClose={onClose}
        />
      )}
      {setup && (
        <ExistingSetupDialog
          orgId={orgId}
          config={config}
          open={open}
          onClose={onClose}
        />
      )}
    </>
  );
};

export const SlackSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config, , error] = useSlackConfig(org.id, userId);
  useErrorLogger('Slack Config error', error);
  const cfg = config || null;

  return (
    <>
      <IntegrationConfigurationButton
        functionalities={functionalities}
        name={NAME}
        logoUrl="/images/integrations-logos/slack.svg"
        active={!!cfg?.data.active}
        interactive={interactive}
        renderSetupDialog={({
          open,
          onClose,
          onFailure,
          onSuccessfulIntegration
        }: IntegrationSetupDialogProperties) => (
          <SetupDialog
            orgId={org.id}
            config={cfg}
            open={open}
            onClose={onClose}
            onFailure={onFailure}
            onSuccessfulIntegration={onSuccessfulIntegration}
          />
        )}
      />
    </>
  );
};
